import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Home, Loader2, CheckCircle2, Wrench, MapPin, User, Phone, Mail, AlertCircle } from "lucide-react";

const STATUS_UI = {
  dispatched: { label: "Awaiting your response", cls: "bg-amber-100 text-amber-800" },
  accepted: { label: "You accepted this job", cls: "bg-blue-100 text-blue-800" },
  completed: { label: "Marked completed", cls: "bg-emerald-100 text-emerald-800" },
};

export default function DispatchResponse() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const presetAction = params.get("action");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("This link is missing its token."); setLoading(false); return; }
    api.get(`/dispatch/${token}`)
      .then((r) => setJob(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This dispatch link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dispatch/${token}/respond`, { action });
      setJob((j) => ({ ...j, status: data.status }));
      toast.success(action === "accept" ? "Thanks — the landlord has been notified you accepted." : "Thanks — marked completed. Everyone has been notified.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the job.");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-bone"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-bone px-4">
      <div className="bg-white border border-line rounded-2xl p-8 max-w-md text-center" data-testid="dispatch-error">
        <AlertCircle className="h-8 w-8 text-terracotta mx-auto" />
        <h1 className="text-xl font-heading text-ink mt-3">Link unavailable</h1>
        <p className="text-sm text-mute mt-2">{error}</p>
        <Link to="/" className="inline-block mt-4 text-sm text-forest hover:underline">Go to LocalLease home</Link>
      </div>
    </div>
  );

  const st = STATUS_UI[job.status] || STATUS_UI.dispatched;
  const done = job.status === "completed";

  return (
    <div className="min-h-screen bg-bone py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6"><Home className="h-6 w-6 text-forest" /><span className="font-heading text-xl text-ink">LocalLease</span></div>

        <div className="bg-white border border-line rounded-2xl overflow-hidden" data-testid="dispatch-card">
          <div className="bg-forest text-white p-6">
            <p className="text-sm opacity-90">{job.dispatched_by} sent you a maintenance job</p>
            <h1 className="text-2xl font-heading mt-1 flex items-center gap-2"><Wrench className="h-5 w-5" /> {job.title}</h1>
            <span data-testid="dispatch-status" className={`inline-block mt-3 text-xs font-medium rounded-full px-3 py-1 ${st.cls}`}>{st.label}</span>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-mute">Property</p>
              <p className="text-ink font-medium">{job.property_title}</p>
              <p className="text-sm text-mute flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {job.address}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-mute">Priority</p>
                <p className="text-ink">{job.priority}</p>
              </div>
              {job.trade && (
                <div><p className="text-xs uppercase tracking-wide text-mute">Trade</p><p className="text-ink">{job.trade}</p></div>
              )}
            </div>

            {job.description && (
              <div><p className="text-xs uppercase tracking-wide text-mute">Details</p><p className="text-ink text-sm whitespace-pre-line">{job.description}</p></div>
            )}

            {job.note && (
              <div className="bg-bone/60 rounded-lg p-3"><p className="text-xs uppercase tracking-wide text-mute">Note from {job.dispatched_by}</p><p className="text-ink text-sm italic">"{job.note}"</p></div>
            )}

            <div className="border-t border-line pt-4">
              <p className="text-xs uppercase tracking-wide text-mute mb-2">Tenant contact</p>
              <div className="space-y-1 text-sm text-ink">
                <p className="flex items-center gap-2"><User className="h-4 w-4 text-forest" /> {job.tenant_name || "—"}</p>
                {job.tenant_phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-forest" /> <a href={`tel:${job.tenant_phone}`} className="hover:underline">{job.tenant_phone}</a></p>}
                {job.tenant_email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-forest" /> <a href={`mailto:${job.tenant_email}`} className="hover:underline">{job.tenant_email}</a></p>}
              </div>
            </div>

            {!done && (
              <div className="flex flex-wrap gap-3 pt-2">
                {job.status !== "accepted" && (
                  <button data-testid="dispatch-accept" onClick={() => respond("accept")} disabled={busy} className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 border border-forest text-forest rounded-lg py-2.5 text-sm font-medium hover:bg-forest/5 disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Accept job
                  </button>
                )}
                <button data-testid="dispatch-complete" onClick={() => respond("complete")} disabled={busy} className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 bg-forest text-white rounded-lg py-2.5 text-sm font-medium hover:bg-forest-hover disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark completed
                </button>
              </div>
            )}
            {done && (
              <div data-testid="dispatch-done" className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-lg p-3 text-sm">
                <CheckCircle2 className="h-5 w-5" /> This job is marked completed. Thank you!
              </div>
            )}
          </div>
        </div>

        {presetAction && !done && (
          <p className="text-center text-xs text-mute mt-4">Tip: tap the button above to confirm you want to {presetAction === "accept" ? "accept" : "complete"} this job.</p>
        )}
      </div>
    </div>
  );
}
